import { Field, Input, Toggle } from "../../../components/Shared";
import { ENUM_TYPES, RANGE_TYPES, TEXT_TYPES } from "./terms";

// The rules of a column's values, shared by a file column's editor (ColumnEditor) and a
// hand-defined table's column (dbdesign/HandTablesPanel): the Required / Unique toggles and the
// "Rules for the values" fieldset (Frictionless constraints: minimum/maximum, lengths, pattern,
// allowed values). Only the rules
// that fit `type` are shown (terms.js RANGE_TYPES / TEXT_TYPES / ENUM_TYPES; no range for time or
// duration); the caller turns `rules` into constraints with rulesPatch(rules, type), which drops
// the rest. The backend checks them again (validation/constraints.py) and answers 422.
//
//   props: type, rules (terms.rulesState), setRule(key) -> (value) => void, columnName,
//          beside (optional: shown left of the toggles, in the same grid row)

// min(220px, 100%) and the fieldset's minWidth 0: both shrink with a narrow panel (320 px, 400 %
// zoom) instead of keeping a 220 px track and the fieldset's default min-content width.
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 16 };

export default function ColumnRules({ type, rules, setRule, columnName, beside = null }) {
  const ruleInput = key => e => setRule(key)(e.target.value);
  return (<>
    <div style={grid}>
      {beside}
      <div style={{ display: "flex", gap: 24, alignItems: "center", flexWrap: "wrap", paddingTop: beside ? 18 : 0 }}>
        <span style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <Toggle checked={rules.required} onChange={setRule("required")} label={`${columnName} is required`} /> Required
        </span>
        <span style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14 }}>
          <Toggle checked={rules.unique} onChange={setRule("unique")} label={`${columnName} is unique`} /> Unique
        </span>
      </div>
    </div>
    {(RANGE_TYPES.includes(type) || TEXT_TYPES.includes(type) || ENUM_TYPES.includes(type)) && (
      <fieldset style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 14px", margin: 0, minWidth: 0 }}>
        <legend style={{ fontSize: 14, fontWeight: 600, padding: "0 6px" }}>Rules for the values</legend>
        <p style={{ fontSize: 13, color: "#4b5563", margin: "0 0 12px" }}>
          Checked before a build (the rows that break a rule are listed) and built into the project
          database as CHECK constraints. Leave a field empty for no rule.
        </p>
        <div style={grid}>
          {RANGE_TYPES.includes(type) && (<>
            <Field label="Minimum" hint={type.startsWith("date") ? "e.g. 2020-01-01" : "The smallest value allowed"}>
              <Input value={rules.minimum} onChange={ruleInput("minimum")} />
            </Field>
            <Field label="Maximum" hint={type.startsWith("date") ? "e.g. 2030-12-31" : "The largest value allowed, e.g. 14 for pH"}>
              <Input value={rules.maximum} onChange={ruleInput("maximum")} />
            </Field>
          </>)}
          {TEXT_TYPES.includes(type) && (<>
            <Field label="Minimum length" hint="Characters">
              <Input value={rules.minLength} onChange={ruleInput("minLength")} inputMode="numeric" />
            </Field>
            <Field label="Maximum length" hint="Characters">
              <Input value={rules.maxLength} onChange={ruleInput("maxLength")} inputMode="numeric" />
            </Field>
            <Field label="Pattern" hint="A regular expression the whole value matches, e.g. S[0-9]{3}">
              <Input value={rules.pattern} onChange={ruleInput("pattern")} />
            </Field>
          </>)}
          {ENUM_TYPES.includes(type) && (
            <Field label="Allowed values" hint="Comma-separated; empty: any value">
              <Input value={rules.enum} onChange={ruleInput("enum")} />
            </Field>
          )}
        </div>
      </fieldset>
    )}
  </>);
}
